import { jsx, jsxs } from "react/jsx-runtime";
import { usePrevious } from "@radix-ui/react-use-previous";
import { useComposedRefs } from "@tamagui/compose-refs";
import {
  getVariableValue,
  isWeb,
  styled,
  withStaticProperties
} from "@tamagui/core";
import { createContextScope } from "@tamagui/create-context";
import { registerFocusable } from "@tamagui/focusable";
import { getSize } from "@tamagui/get-token";
import { useLabelContext } from "@tamagui/label";
import { ThemeableStack, XStack } from "@tamagui/stacks";
import { useControllableState } from "@tamagui/use-controllable-state";
import * as React from "react";
import {
  Switch as NativeSwitch,
  Platform
} from "react-native";
const SWITCH_NAME = "Switch";
const getSwitchHeight = (val) => Math.round(getVariableValue(getSize(val)) * 0.65);
const getSwitchWidth = (val) => getSwitchHeight(val) * 2;
const scopeContexts = createContextScope(SWITCH_NAME);
const [createSwitchContext] = scopeContexts;
const createSwitchScope = scopeContexts[1];
const [SwitchProvider, useSwitchContext] = createSwitchContext(SWITCH_NAME);
const THUMB_NAME = "SwitchThumb";
const SwitchThumbFrame = styled(ThemeableStack, {
  name: "SwitchThumb",
  variants: {
    unstyled: {
      false: {
        size: "$true",
        backgroundColor: "$background",
        borderRadius: 1e3
      }
    },
    size: {
      "...size": (val) => {
        const size = getSwitchHeight(val);
        return {
          height: size,
          width: size
        };
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const SwitchThumb = SwitchThumbFrame.extractable(
  React.forwardRef(
    (props, forwardedRef) => {
      const { __scopeSwitch, size: sizeProp, ...thumbProps } = props;
      const {
        size: sizeContext,
        disabled,
        checked,
        unstyled
      } = useSwitchContext(THUMB_NAME, __scopeSwitch);
      const size = sizeProp ?? sizeContext;
      return /* @__PURE__ */ jsx(
        SwitchThumbFrame,
        {
          unstyled,
          theme: checked ? "active" : null,
          size,
          "data-state": getState(checked),
          "data-disabled": disabled ? "" : void 0,
          ...thumbProps,
          x: checked ? getVariableValue(getSwitchWidth(size)) - getVariableValue(getSwitchHeight(size)) : 0,
          ref: forwardedRef
        }
      );
    }
  )
);
SwitchThumb.displayName = THUMB_NAME;
const SwitchFrame = styled(XStack, {
  name: SWITCH_NAME,
  tag: "button",
  variants: {
    unstyled: {
      false: {
        size: "$true",
        borderRadius: 1e3,
        borderWidth: 2,
        borderColor: "transparent",
        backgroundColor: "$background",
        focusStyle: {
          borderColor: "$borderColorFocus",
          outlineColor: "$borderColorFocus",
          outlineStyle: "solid",
          outlineWidth: 1
        }
      }
    },
    size: {
      "...size": (val) => {
        const height = getSwitchHeight(val) + 4;
        const width = getSwitchWidth(val) + 4;
        return {
          height,
          minHeight: height,
          width
        };
      }
    }
  },
  defaultVariants: {
    unstyled: false
  }
});
const Switch = withStaticProperties(
  SwitchFrame.extractable(
    React.forwardRef(
      (props, forwardedRef) => {
        const {
          __scopeSwitch,
          labeledBy: ariaLabelledby,
          name,
          checked: checkedProp,
          defaultChecked,
          required,
          disabled,
          value = "on",
          onCheckedChange,
          size = "$true",
          unstyled = false,
          native: nativeProp,
          nativeProps,
          ...switchProps
        } = props;
        const native = Array.isArray(nativeProp) ? nativeProp : [nativeProp];
        const shouldRenderMobileNative = !isWeb && nativeProp === true || native.includes("mobile") || native.includes("android") && Platform.OS === "android" || native.includes("ios") && Platform.OS === "ios";
        if (shouldRenderMobileNative) {
          return /* @__PURE__ */ jsx(
            NativeSwitch,
            {
              value: checkedProp,
              onValueChange: onCheckedChange,
              ...nativeProps
            }
          );
        }
        const [button, setButton] = React.useState(null);
        const composedRefs = useComposedRefs(
          forwardedRef,
          (node) => setButton(node)
        );
        const labelId = useLabelContext(button);
        const labelledBy = ariaLabelledby || labelId;
        const hasConsumerStoppedPropagationRef = React.useRef(false);
        const isFormControl = isWeb ? button ? Boolean(button.closest("form")) : true : false;
        const [checked = false, setChecked] = useControllableState({
          prop: checkedProp,
          defaultProp: defaultChecked || false,
          onChange: onCheckedChange,
          transition: true
        });
        if (!isWeb) {
          React.useEffect(() => {
            if (!props.id)
              return;
            return registerFocusable(props.id, {
              focus: () => {
                setChecked((x) => !x);
              }
            });
          }, [props.id, setChecked]);
        }
        return /* @__PURE__ */ jsxs(
          SwitchProvider,
          {
            scope: __scopeSwitch,
            checked,
            disabled,
            size,
            unstyled,
            children: [
              /* @__PURE__ */ jsx(
                SwitchFrame,
                {
                  unstyled,
                  size,
                  theme: checked ? "active" : null,
                  role: "switch",
                  "aria-checked": checked,
                  "aria-labelledby": labelledBy,
                  "aria-required": required,
                  "data-state": getState(checked),
                  "data-disabled": disabled ? "" : void 0,
                  disabled,
                  tabIndex: disabled ? void 0 : 0,
                  value,
                  ...switchProps,
                  ref: composedRefs,
                  onPress: (event) => {
                    var _a;
                    (_a = props.onPress) == null ? void 0 : _a.call(props, event);
                    setChecked((prevChecked) => !prevChecked);
                    if (isWeb && isFormControl) {
                      hasConsumerStoppedPropagationRef.current = event.isPropagationStopped();
                      if (!hasConsumerStoppedPropagationRef.current)
                        event.stopPropagation();
                    }
                  }
                }
              ),
              isWeb && isFormControl && /* @__PURE__ */ jsx(
                BubbleInput,
                {
                  control: button,
                  bubbles: !hasConsumerStoppedPropagationRef.current,
                  name,
                  value,
                  checked,
                  required,
                  disabled,
                  style: { transform: "translateX(-100%)" }
                }
              )
            ]
          }
        );
      }
    )
  ),
  {
    Thumb: SwitchThumb
  }
);
const BubbleInput = (props) => {
  const { control, checked, bubbles = true, ...inputProps } = props;
  const ref = React.useRef(null);
  const prevChecked = usePrevious(checked);
  React.useEffect(() => {
    const input = ref.current;
    const inputProto = window.HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(
      inputProto,
      "checked"
    );
    const setChecked = descriptor.set;
    if (prevChecked !== checked && setChecked) {
      const event = new Event("click", { bubbles });
      setChecked.call(input, checked);
      input.dispatchEvent(event);
    }
  }, [prevChecked, checked, bubbles]);
  return /* @__PURE__ */ jsx(
    "input",
    {
      type: "checkbox",
      "aria-hidden": true,
      defaultChecked: checked,
      ...inputProps,
      tabIndex: -1,
      ref,
      style: {
        ...props.style,
        // ...controlSize,
        position: "absolute",
        pointerEvents: "none",
        opacity: 0,
        margin: 0
      }
    }
  );
};
function getState(checked) {
  return checked ? "checked" : "unchecked";
}
export {
  Switch,
  SwitchFrame,
  SwitchThumb,
  SwitchThumbFrame,
  createSwitchScope
};
//# sourceMappingURL=Switch.js.map
